import { auth } from '@/state/auth'
import axios from 'axios'
import { produce } from 'immer'
import { create } from 'zustand'
import type { ApiCommitsResponse } from '../../functions/api/repositories/commits'

type RepoCommits = {
  list: ApiCommitsResponse['list']
  hasMore: boolean
  page: number
}

type CommitsState = {
  loading: Record<number, boolean | undefined>
  byRepoId: Record<number, RepoCommits | undefined>
  fetch(
    repoId: number,
    owner: string,
    repoName: string,
    page?: number,
    branch?: string,
  ): Promise<void>
  reset(): void
  setProcessed(data: { repoId: number; sha: string }[]): void
  setPage(repoId: number, page: number): void
}

export const useCommitsState = create<CommitsState>(set => ({
  byRepoId: {},
  loading: {},

  setProcessed: data => {
    set(produce((s: CommitsState) => {
      data.forEach(({ repoId, sha }) => {
        const commit = s.byRepoId[repoId]?.list.find(commit => commit.commit.sha === sha)
        if (commit) {
          commit.state.processed = true
        }
      })
    }))
  },

  setPage: (repoId, page) => {
    set(produce((s: CommitsState) => {
      if (s.byRepoId[repoId]) {
        s.byRepoId[repoId].page = page
      }
    }))
  },

  fetch: async (repoId, owner, repoName, page = 1, branch) => {
    set(produce((s: CommitsState) => {
      s.loading[repoId] = true
    }))

    try {
      const { data } = await auth(token => (
        axios.get<ApiCommitsResponse>('/api/repositories/commits', {
          headers: { Authorization: `Bearer ${token}` },
          params: { owner, repo: repoName, page, per_page: 20, branch },
        })
      ))

      set(produce((s: CommitsState) => {
        s.byRepoId[repoId] = data
      }))
    }
    catch (e) {
      console.error(e)
    }
    finally {
      set(produce((s: CommitsState) => {
        s.loading[repoId] = false
      }))
    }
  },

  reset: () => {
    set({
      byRepoId: {},
      loading: {},
    })
  },
}))
